import React from 'react';

const Hero = () => {
  const scrollToEligibility = () => {
    document.getElementById('eligibility')?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <section id="hero" className="bg-background-light min-h-[calc(100vh-4rem)] flex items-center overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="animate-slide-up">
            {/* Tagline */}
            <span className="inline-block text-primary-green text-sm font-semibold tracking-wider uppercase mb-4">
              Research based
            </span>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Fast and Reliable{' '}
              <span className="text-primary-green">Credit for You</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-gray-600 mb-8 max-w-lg">
              CreditGO leverages advanced predictive algorithms to evaluate your business credit eligibility in real-time. <br/>
              Make informed financial decisions with data-driven insights.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToEligibility}
                className="px-8 py-4 bg-gray-900 text-white font-semibold rounded-full shadow-lg hover:bg-gray-800 transform hover:-translate-y-0.5 transition-all"
              >
                Check Eligibility
              </button>
              <button
                onClick={scrollToHowItWorks}
                className="px-8 py-4 bg-primary-green text-white font-semibold rounded-full shadow-lg hover:bg-primary-green-light transform hover:-translate-y-0.5 transition-all"
              >
                How It Works
              </button>
            </div>
          </div>

          {/* Right Content - Decorative Images */}
          <div className="relative hidden lg:block h-[500px]">
            {/* Floating money bill - top right */}
            <div className="absolute -top-4 right-8 animate-float" style={{ animationDelay: '0s', willChange: 'transform' }}>
              <svg className="w-20 h-12 text-primary-green opacity-60" viewBox="0 0 100 60" fill="currentColor">
                <rect x="5" y="5" width="90" height="50" rx="4" fill="#22c55e" stroke="#16a34a" strokeWidth="2"/>
                <circle cx="50" cy="30" r="12" fill="#16a34a"/>
                <text x="50" y="35" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">$</text>
              </svg>
            </div>

            {/* Floating money bill - top left */}
            <div className="absolute top-16 -left-4 animate-float" style={{ animationDelay: '1s', willChange: 'transform' }}>
              <svg className="w-16 h-10 text-primary-green opacity-50 rotate-12" viewBox="0 0 100 60" fill="currentColor">
                <rect x="5" y="5" width="90" height="50" rx="4" fill="#22c55e" stroke="#16a34a" strokeWidth="2"/>
                <circle cx="50" cy="30" r="12" fill="#16a34a"/>
                <text x="50" y="35" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">$</text>
              </svg>
            </div>

            {/* Main Credit Card - Back */}
            <div
              className="absolute top-20 right-0 w-72 h-44 rounded-2xl shadow-2xl transform rotate-6 animate-fade-in-scale"
              style={{
                background: 'linear-gradient(135deg, #2c6e49 0%, #3fa34d 100%)',
                animationDelay: '0.2s'
              }}
            >
              <div className="p-6 h-full flex flex-col justify-between text-white">
                <div className="flex justify-between items-start">
                  <span className="text-lg font-bold">BANK</span>
                  <div className="w-10 h-8 bg-white/20 rounded"></div>
                </div>
                <div>
                  <div className="text-xs opacity-70 mb-1">CREDIT CARD</div>
                  <div className="font-mono text-sm tracking-wider">0000 0000 0065 004</div>
                </div>
                <div className="flex justify-between text-xs">
                  <div>
                    <div className="opacity-70">VALID</div>
                    <div>02/27</div>
                  </div>
                  <div>
                    <div className="opacity-70">CVV</div>
                    <div>123</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Credit Card - Front */}
            <div
              className="absolute top-48 left-8 w-72 h-44 rounded-2xl shadow-2xl transform -rotate-6 animate-fade-in-scale"
              style={{
                background: 'linear-gradient(135deg, #3fa34d 0%, #2c6e49 100%)',
                animationDelay: '0.4s'
              }}
            >
              <div className="p-6 h-full flex flex-col justify-between text-white">
                <div className="flex justify-between items-start">
                  <span className="text-lg font-bold">BANK</span>
                  <div className="flex gap-1">
                    <div className="w-6 h-6 bg-yellow-400 rounded-full opacity-80"></div>
                    <div className="w-6 h-6 bg-orange-500 rounded-full opacity-80 -ml-3"></div>
                  </div>
                </div>
                <div className="w-12 h-9 bg-yellow-300/80 rounded"></div>
                <div>
                  <div className="font-mono text-sm tracking-wider mb-2">**** **** **** 1234</div>
                  <div className="text-xs opacity-70">CARDHOLDER NAME</div>
                </div>
              </div>
            </div>

            {/* Floating money bill - bottom right */}
            <div className="absolute bottom-12 right-4 animate-float" style={{ animationDelay: '0.5s', willChange: 'transform' }}>
              <svg className="w-14 h-8 text-primary-green opacity-40 -rotate-12" viewBox="0 0 100 60" fill="currentColor">
                <rect x="5" y="5" width="90" height="50" rx="4" fill="#22c55e" stroke="#16a34a" strokeWidth="2"/>
                <circle cx="50" cy="30" r="12" fill="#16a34a"/>
                <text x="50" y="35" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">$</text>
              </svg>
            </div>

            {/* Decorative dots */}
            <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-gray-300 rounded-full"></div>
            <div className="absolute top-1/3 left-1/4 w-3 h-3 bg-gray-200 rounded-full"></div>
            <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-primary-green/30 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
